// CinematicProcessingOverlay.js — Full-screen cinematic experience after job creation
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const WAVEFORM_BARS = 32;
const LANGUAGES_TICKER = ['ES', 'FR', 'DE', 'JA', 'HI', 'AR', 'ZH', 'PT', 'KO', 'IT', 'RU', 'TR'];

function WaveformBar({ index, color }) {
  const delay = (index / WAVEFORM_BARS) * 0.6;
  const heights = [0.3, 0.7, 0.5, 1.0, 0.4, 0.85, 0.6, 0.45, 0.9, 0.35];
  const peak = heights[index % heights.length];

  return (
    <motion.div
      style={{
        width: 3,
        borderRadius: 4,
        background: `linear-gradient(to top, ${color}60, ${color})`,
        flexShrink: 0,
      }}
      animate={{
        height: ['8px', `${peak * 52 + 8}px`, '8px', `${peak * 30 + 8}px`, '8px'],
      }}
      transition={{
        duration: 1.2 + (index % 5) * 0.18,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}

function LanguageTicker({ color }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % LANGUAGES_TICKER.length), 260);
    return () => clearInterval(t);
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
      {LANGUAGES_TICKER.map((lang, i) => (
        <motion.div
          key={lang}
          animate={{
            opacity: i === idx ? 1 : i === (idx - 1 + LANGUAGES_TICKER.length) % LANGUAGES_TICKER.length ? 0.5 : 0.15,
            scale: i === idx ? 1.1 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <Box sx={{
            px: 1.2, py: 0.3, borderRadius: '6px',
            background: i === idx ? `${color}28` : 'transparent',
            border: `1px solid ${i === idx ? color + '60' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <Typography sx={{
              fontSize: '11px', fontWeight: 700, fontFamily: 'Inter, sans-serif',
              color: i === idx ? color : 'rgba(255,255,255,0.3)',
              letterSpacing: '0.05em',
            }}>
              {lang}
            </Typography>
          </Box>
        </motion.div>
      ))}
    </Box>
  );
}

export default function CinematicProcessingOverlay({ job, open, onClose }) {
  const [progress, setProgress] = useState(0);
  const DISPLAY_DURATION = 5000; // ms

  useEffect(() => {
    if (!open) { setProgress(0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const pct = Math.min((now - start) / DISPLAY_DURATION, 1);
      setProgress(pct);
      if (pct < 1) raf = requestAnimationFrame(tick);
      else onClose?.();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, onClose]);

  const color = '#a78bfa';
  const targetLang = job?.target_language || job?.language || 'your language';
  const jobName = job?.name || job?.video_name || 'your video';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.96)',
            backdropFilter: 'blur(20px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={onClose}
        >
          {/* Background glow */}
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600, height: 600, borderRadius: '50%',
            background: `radial-gradient(circle, ${color}18 0%, transparent 65%)`,
            pointerEvents: 'none',
          }} />

          <Box sx={{ textAlign: 'center', px: 4, position: 'relative', maxWidth: 560 }}>

            {/* Status pill */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
            >
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                px: 2, py: 0.7, borderRadius: '100px', mb: 4,
                background: `${color}18`,
                border: `1px solid ${color}40`,
              }}>
                <motion.div
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: color, flexShrink: 0,
                  }}
                />
                <Typography sx={{
                  fontSize: '12px', fontWeight: 600, fontFamily: 'Inter, sans-serif',
                  color, letterSpacing: '0.05em', textTransform: 'uppercase',
                }}>
                  AI Dubbing in Progress
                </Typography>
              </Box>
            </motion.div>

            {/* Waveform */}
            <motion.div
              initial={{ opacity: 0, scaleY: 0 }}
              animate={{ opacity: 1, scaleY: 1 }}
              transition={{ delay: 0.3, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <Box sx={{
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                gap: '3px', height: 68, mb: 5,
              }}>
                {Array.from({ length: WAVEFORM_BARS }, (_, i) => (
                  <WaveformBar key={i} index={i} color={color} />
                ))}
              </Box>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Typography sx={{
                fontFamily: 'Inter, sans-serif', fontWeight: 800,
                fontSize: { xs: '1.6rem', md: '2rem' },
                color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.2, mb: 1,
              }}>
                Translating your voice
              </Typography>
              <Typography sx={{
                color: 'rgba(255,255,255,0.38)', fontSize: '14px',
                fontFamily: 'Inter, sans-serif', mb: 0.5,
              }}>
                {jobName}
              </Typography>
              <Typography sx={{
                color, fontSize: '13px', fontFamily: 'Inter, sans-serif', fontWeight: 600,
              }}>
                → {targetLang}
              </Typography>
            </motion.div>

            {/* Language ticker */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <LanguageTicker color={color} />
            </motion.div>

            {/* Progress bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <Box sx={{ mt: 5, px: 2 }}>
                <Box sx={{
                  height: 2, borderRadius: '2px',
                  background: 'rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}>
                  <Box sx={{
                    height: '100%', borderRadius: '2px',
                    background: `linear-gradient(90deg, ${color}, #60a5fa)`,
                    width: `${progress * 100}%`,
                    transition: 'width 0.1s linear',
                    boxShadow: `0 0 12px ${color}80`,
                  }} />
                </Box>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.2)', fontSize: '11px',
                  fontFamily: 'Inter, sans-serif', mt: 1.5,
                }}>
                  Click anywhere to dismiss · This runs in the background
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
